import { useState } from 'react';
import { ConfigProvider, Tooltip } from 'antd';
import cn from 'classnames';

import { Icon, Text } from 'shared/components/ui';
import { SearchSelect } from 'features/search/components';
import { useGetProgrammingLanguagesQuery, useGetRepositoriesQuery, useGetSkillsQuery } from 'services';

import styles from './index.module.scss';

export const SearchByV2 = () => {
	const [activeTab, setActiveTab] = useState<'github' | 'stackoverflow' | 'kaggle' | null>('github');
	const tooltipPlacement = 'right';

	return (
		<ConfigProvider
			theme={{
				components: {
					Tooltip: {
						paddingSM: 0,
						paddingXS: 0,
						borderRadius: 2,
						colorBgDefault: '#f8f8f8'
					}
				}
			}}>
			<div
				className={cn(styles.wrapper, {
					[styles.wrapperStackoverflow]: activeTab === 'stackoverflow'
				})}>
				<div className={cn(styles.tabsWrapper, { [styles.tabsWrapperActive]: activeTab !== null })}>
					<div className={styles.tabsHeader}>
						<Text variant="inter/15/medium">Search by</Text>
					</div>
					<div className={styles.tabs}>
						<div
							onClick={() => {
								setActiveTab('github');
							}}
							className={cn(styles.tab, { [styles.tabGithubActive]: activeTab === 'github' })}>
							<div className={styles.tabGithubLogoWrapper}>
								<Icon icon="github-logo-circle" className={styles.tabGithubLogo} size={20} />
							</div>
							<Text variant="inter/15/medium">Github</Text>
						</div>
						<div
							onClick={() => {
								setActiveTab('stackoverflow');
							}}
							className={cn(styles.tab, {
								[styles.tabStackoverflowActive]: activeTab === 'stackoverflow'
							})}>
							<Icon icon="stackoverflow-logo" size={20} />
							<Text variant="inter/15/medium">Stack Overflow</Text>
						</div>
						<div className={cn(styles.tab, styles.tabDisabled)}>
							<Icon icon="kaggle" size={20} />
							<Text variant="inter/15/medium">
								Kaggle <span className={styles.coming}>(coming soon)</span>
							</Text>
						</div>
					</div>
				</div>
				{activeTab === 'github' && (
					<div className={styles.section}>
						<SearchSelect
							label={
								<div className={styles.label}>
									<Text variant="inter/15/medium">Required skills</Text>
									<Tooltip
										arrow={false}
										title={
											<div className={styles.tooltip}>
												<Text color="dark_green">
													Use multiple skills to&nbsp;describe your perfect candidate. Skills
													are more detailed than programming languages, include technical
													concepts, names of&nbsp;specific technologies, libraries
													or&nbsp;areas of&nbsp;computer science like &lsquo;deep
													learning&rsquo; or &lsquo;computer vision&rsquo;,
													as&nbsp;an&nbsp;example.
													<br />
													<br />
													Required skills are connected through AND, that means the more
													skills you require, the less candidates will be in the result.
												</Text>
											</div>
										}
										placement={tooltipPlacement}
										overlayClassName={styles.labelTooltip}>
										<Icon icon="info" className={styles.labelIcon} />
									</Tooltip>
								</div>
							}
							className={styles.selectGithub}
							name="required_skills"
							useQuery={useGetSkillsQuery}
							placeholder="Enter or select Required skills"
							type="github"
						/>
						<SearchSelect
							label={
								<div className={styles.label}>
									<Text variant="inter/15/medium">Bonus skills</Text>
									<Tooltip
										arrow={false}
										title={
											<div className={styles.tooltip}>
												<Text color="dark_green">
													Bonus skills are connected through OR, that means the more skills
													you add here, the more candidates will be in the result.
												</Text>
											</div>
										}
										placement={tooltipPlacement}
										overlayClassName={styles.labelTooltip}>
										<Icon icon="info" className={styles.labelIcon} />
									</Tooltip>
								</div>
							}
							className={styles.selectGithub}
							name="bonus_skills"
							useQuery={useGetSkillsQuery}
							placeholder="Enter or select Bonus skills"
							type="github"
						/>
						<SearchSelect
							label={
								<div className={styles.label}>
									<Text variant="inter/15/medium">Programming languages</Text>
									<Tooltip
										arrow={false}
										title={
											<div className={styles.tooltip}>
												<Text color="dark_green">
													Programming languages are Python, C++, JavaScript, etc.
												</Text>
											</div>
										}
										placement={tooltipPlacement}
										overlayClassName={styles.labelTooltip}>
										<Icon icon="info" className={styles.labelIcon} />
									</Tooltip>
								</div>
							}
							className={styles.selectGithub}
							name="prog_langs"
							useQuery={useGetProgrammingLanguagesQuery}
							placeholder="Enter or select Programming Languages"
							type="github"
						/>
						<SearchSelect
							className={styles.selectGithub}
							label="GitHub Repositories"
							name="github_repos"
							useQuery={useGetRepositoriesQuery}
							placeholder="Select GitHub Repositories"
							type="github"
						/>
					</div>
				)}
				{activeTab === 'stackoverflow' && (
					<div className={styles.section}>
						<SearchSelect
							label={<Text variant="inter/15/medium">Stack Overflow skills</Text>}
							className={styles.selectStackoverflow}
							name="so_skills"
							useQuery={useGetSkillsQuery}
							placeholder="Enter or select Stack Overflow skills"
							type="stackoverflow"
						/>
					</div>
				)}
			</div>
		</ConfigProvider>
	);
};
